// ============================================================================
// Offline Store - IndexedDB Service for Assessment Data
// EduPath Africa v2.0 - Sprint 3: Offline Assessment Infrastructure
// ============================================================================
//
// FILE: src/services/offlineStore.ts
// ACTION: Add this file to your existing services directory.
//
// Provides IndexedDB-backed storage for:
//   - Cached question banks (downloaded while online)
//   - In-progress assessment sessions (saved offline)
//   - Queued submissions (sync when back online)
//   - Cached user profile/auth data
//
// Uses raw IndexedDB API (no idb library needed) for zero dependencies.
// ============================================================================

const DB_NAME = 'edupath-offline';
const DB_VERSION = 5;

// Store names
const STORES = {
  QUESTION_BANKS: 'questionBanks',       // Cached question sets by assessment type
  SESSIONS: 'offlineSessions',           // In-progress offline sessions
  SYNC_QUEUE: 'syncQueue',               // Queued API calls for background sync
  CHECKPOINTS: 'checkpoints',            // Auto-saved checkpoints
  META: 'meta',                          // App metadata (last sync, user info, etc.)
} as const;

// ============================================================================
// Types
// ============================================================================

export interface CachedQuestionBank {
  assessmentType: string;
  questions: CachedQuestion[];
  cachedAt: string;         // ISO timestamp
  version: string;          // Question bank version for cache invalidation
  questionCount: number;
}

export interface CachedQuestion {
  id: string;
  text: string;
  type: 'likert' | 'multiple_choice';
  options?: string[];
  dimension?: string;
  assessmentType: string;
}

export interface OfflineSession {
  sessionId: string;          // Client-generated UUID for offline sessions
  learnerId: string;
  assessmentType: string;
  questions: CachedQuestion[];
  responses: OfflineResponse[];
  currentQuestionIndex: number;
  questionOrder: string[];
  createdAt: string;
  updatedAt: string;
  status: 'ACTIVE' | 'COMPLETED' | 'SYNCED' | 'SYNC_FAILED';
  isOfflineCreated: boolean;  // true = created offline, false = started online
  serverSessionId?: string;   // Populated after sync creates server session
  totalQuestions: number;
}

export interface OfflineResponse {
  questionId: string;
  response: string | number;
  answeredAt: string;
}

export interface SyncQueueItem {
  id: string;                 // Auto-generated
  type: 'SUBMIT_SESSION' | 'SAVE_CHECKPOINT' | 'CREATE_SESSION';
  payload: Record<string, unknown>;
  createdAt: string;
  attempts: number;
  lastAttemptAt?: string;
  lastError?: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  sessionId: string;          // Reference to offline session
}

export interface OfflineCheckpoint {
  sessionId: string;
  responses: OfflineResponse[];
  currentQuestionIndex: number;
  savedAt: string;
}

// ============================================================================
// Database Initialization
// ============================================================================

let dbPromise: Promise<IDBDatabase> | null = null;

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Question banks - keyed by assessmentType
      if (!db.objectStoreNames.contains(STORES.QUESTION_BANKS)) {
        db.createObjectStore(STORES.QUESTION_BANKS, { keyPath: 'assessmentType' });
      }

      // Offline sessions - keyed by sessionId
      if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
        const sessionStore = db.createObjectStore(STORES.SESSIONS, { keyPath: 'sessionId' });
        sessionStore.createIndex('learnerId', 'learnerId', { unique: false });
        sessionStore.createIndex('status', 'status', { unique: false });
      }

      // Sync queue - auto-increment key
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
        syncStore.createIndex('status', 'status', { unique: false });
        syncStore.createIndex('sessionId', 'sessionId', { unique: false });
      }

      // Checkpoints - keyed by sessionId
      if (!db.objectStoreNames.contains(STORES.CHECKPOINTS)) {
        db.createObjectStore(STORES.CHECKPOINTS, { keyPath: 'sessionId' });
      }

      // Meta store - key-value
      if (!db.objectStoreNames.contains(STORES.META)) {
        db.createObjectStore(STORES.META, { keyPath: 'key' });
      }
      // Create store needed by offlineQueueService.ts
      if (!db.objectStoreNames.contains('submission-queue')) {
        const sq = db.createObjectStore('submission-queue', { keyPath: 'id' });
        sq.createIndex('status', 'status', { unique: false });
        sq.createIndex('sessionId', 'sessionId', { unique: false });
        sq.createIndex('queuedAt', 'queuedAt', { unique: false });
        sq.createIndex('nextRetryAt', 'nextRetryAt', { unique: false });
        sq.createIndex('learnerId', 'learnerId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.error('[OfflineStore] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };
  });

  return dbPromise;
}

// ============================================================================
// Generic helpers
// ============================================================================

async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest
): Promise<T> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const request = fn(store);
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error);
  });
}

async function getAll<T>(storeName: string): Promise<T[]> {
  return withStore(storeName, 'readonly', (store) => store.getAll());
}

async function getByKey<T>(storeName: string, key: string): Promise<T | undefined> {
  return withStore(storeName, 'readonly', (store) => store.get(key));
}

async function putItem<T>(storeName: string, item: T): Promise<void> {
  await withStore(storeName, 'readwrite', (store) => store.put(item));
}

async function deleteItem(storeName: string, key: string): Promise<void> {
  await withStore(storeName, 'readwrite', (store) => store.delete(key));
}

// ============================================================================
// Question Bank Cache
// ============================================================================

export const questionBankCache = {
  /**
   * Cache a question bank for offline use
   */
  async save(assessmentType: string, questions: CachedQuestion[], version = '1.0'): Promise<void> {
    const entry: CachedQuestionBank = {
      assessmentType,
      questions,
      cachedAt: new Date().toISOString(),
      version,
      questionCount: questions.length,
    };
    await putItem(STORES.QUESTION_BANKS, entry);
    console.log(`[OfflineStore] Cached ${questions.length} questions for ${assessmentType}`);
  },

  /**
   * Retrieve cached question bank
   */
  async get(assessmentType: string): Promise<CachedQuestionBank | undefined> {
    return getByKey(STORES.QUESTION_BANKS, assessmentType);
  },

  /**
   * Get all cached question banks
   */
  async getAll(): Promise<CachedQuestionBank[]> {
    return getAll(STORES.QUESTION_BANKS);
  },

  /**
   * Check if a question bank is cached and not stale (< 7 days)
   */
  async isFresh(assessmentType: string, maxAgeDays = 7): Promise<boolean> {
    const cached = await this.get(assessmentType);
    if (!cached) return false;
    const age = Date.now() - new Date(cached.cachedAt).getTime();
    return age < maxAgeDays * 24 * 60 * 60 * 1000;
  },

  /**
   * Clear all cached question banks
   */
  async clear(): Promise<void> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.QUESTION_BANKS, 'readwrite');
      const req = tx.objectStore(STORES.QUESTION_BANKS).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  },
};

// ============================================================================
// Offline Sessions
// ============================================================================

export const offlineSessions = {
  /**
   * Create or update an offline session
   */
  async save(session: OfflineSession): Promise<void> {
    session.updatedAt = new Date().toISOString();
    await putItem(STORES.SESSIONS, session);
  },

  /**
   * Get a session by ID
   */
  async get(sessionId: string): Promise<OfflineSession | undefined> {
    return getByKey(STORES.SESSIONS, sessionId);
  },

  /**
   * Get all sessions for a learner
   */
  async getByLearner(learnerId: string): Promise<OfflineSession[]> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.SESSIONS, 'readonly');
      const store = tx.objectStore(STORES.SESSIONS);
      const index = store.index('learnerId');
      const request = index.getAll(learnerId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  /**
   * Get active (unsent) sessions for a learner
   */
  async getActive(learnerId: string): Promise<OfflineSession[]> {
    const all = await this.getByLearner(learnerId);
    return all.filter(s => s.status === 'ACTIVE' || s.status === 'COMPLETED');
  },

  /**
   * Delete a session
   */
  async delete(sessionId: string): Promise<void> {
    await deleteItem(STORES.SESSIONS, sessionId);
  },

  /**
   * Count pending sessions (not yet synced)
   */
  async countPending(): Promise<number> {
    const all = await getAll<OfflineSession>(STORES.SESSIONS);
    return all.filter(s => s.status === 'COMPLETED').length;
  },
};

// ============================================================================
// Sync Queue
// ============================================================================

export const syncQueue = {
  /**
   * Add an item to the sync queue
   */
  async enqueue(item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'attempts' | 'status'>): Promise<string> {
    const id = `sync-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const queueItem: SyncQueueItem = {
      ...item,
      id,
      createdAt: new Date().toISOString(),
      attempts: 0,
      status: 'PENDING',
    };
    await putItem(STORES.SYNC_QUEUE, queueItem);
    console.log(`[OfflineStore] Enqueued sync item: ${item.type} for session ${item.sessionId}`);
    return id;
  },

  /**
   * Get all pending items (ordered by creation time)
   */
  async getPending(): Promise<SyncQueueItem[]> {
    const all = await getAll<SyncQueueItem>(STORES.SYNC_QUEUE);
    return all
      .filter(item => item.status === 'PENDING' || item.status === 'IN_PROGRESS')
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  },

  /**
   * Update a queue item (e.g., mark as in-progress, failed, completed)
   */
  async update(id: string, updates: Partial<SyncQueueItem>): Promise<void> {
    const existing = await getByKey<SyncQueueItem>(STORES.SYNC_QUEUE, id);
    if (!existing) throw new Error(`Sync queue item ${id} not found`);
    await putItem(STORES.SYNC_QUEUE, { ...existing, ...updates });
  },

  /**
   * Mark item as completed and remove it
   */
  async complete(id: string): Promise<void> {
    await deleteItem(STORES.SYNC_QUEUE, id);
  },

  /**
   * Mark item as failed with error
   */
  async fail(id: string, error: string): Promise<void> {
    const existing = await getByKey<SyncQueueItem>(STORES.SYNC_QUEUE, id);
    if (!existing) return;
    await putItem(STORES.SYNC_QUEUE, {
      ...existing,
      status: existing.attempts >= 5 ? 'FAILED' : 'PENDING',
      attempts: existing.attempts + 1,
      lastAttemptAt: new Date().toISOString(),
      lastError: error,
    });
  },

  /**
   * Get count of pending items
   */
  async count(): Promise<number> {
    const pending = await this.getPending();
    return pending.length;
  },

  /**
   * Clear completed/failed items older than N days
   */
  async cleanup(maxAgeDays = 30): Promise<number> {
    const all = await getAll<SyncQueueItem>(STORES.SYNC_QUEUE);
    const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
    let removed = 0;

    for (const item of all) {
      if (
        (item.status === 'COMPLETED' || item.status === 'FAILED') &&
        new Date(item.createdAt).getTime() < cutoff
      ) {
        await deleteItem(STORES.SYNC_QUEUE, item.id);
        removed++;
      }
    }
    return removed;
  },
};

// ============================================================================
// Checkpoints (quick auto-save)
// ============================================================================

export const checkpoints = {
  async save(checkpoint: OfflineCheckpoint): Promise<void> {
    await putItem(STORES.CHECKPOINTS, checkpoint);
  },

  async get(sessionId: string): Promise<OfflineCheckpoint | undefined> {
    return getByKey(STORES.CHECKPOINTS, sessionId);
  },

  async delete(sessionId: string): Promise<void> {
    await deleteItem(STORES.CHECKPOINTS, sessionId);
  },
};

// ============================================================================
// Meta Store (app-level key-value)
// ============================================================================

export const metaStore = {
  async set(key: string, value: unknown): Promise<void> {
    await putItem(STORES.META, { key, value, updatedAt: new Date().toISOString() });
  },

  async get<T = unknown>(key: string): Promise<T | undefined> {
    const entry = await getByKey<{ key: string; value: T }>(STORES.META, key);
    return entry?.value;
  },

  async delete(key: string): Promise<void> {
    await deleteItem(STORES.META, key);
  },
};

// ============================================================================
// Database Management
// ============================================================================

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Get storage usage estimate
 */
export async function getStorageEstimate(): Promise<{ used: number; quota: number } | null> {
  if (navigator.storage && navigator.storage.estimate) {
    const est = await navigator.storage.estimate();
    return { used: est.usage || 0, quota: est.quota || 0 };
  }
  return null;
}

/**
 * Clear all offline data (nuclear option)
 */
export async function clearAllOfflineData(): Promise<void> {
  const db = await getDB();
  const storeNames = Object.values(STORES);

  for (const storeName of storeNames) {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const req = tx.objectStore(storeName).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
  console.log('[OfflineStore] All offline data cleared');
}

// Export store names for testing
export { STORES };
